import React, { useEffect, useState } from 'react';

/**
 * BCM Agent Swarm — live progress card
 * Renders an 8-agent roster during market analysis with:
 *   - "Head shot" avatar (initials + brand color)
 *   - Name + role
 *   - Status sub-card (Queued → Analyzing → Complete)
 *
 * Works off a simulated timeline that approximately matches the real swarm
 * runtime (~65s) since the backend doesn't stream per-agent progress yet.
 */

const AGENTS = [
  {
    id: 'market_sizing',
    name: 'Dr. Nora Vance',
    role: 'Market Sizing Lead',
    task: 'TAM / SAM / SOM + drivers',
    color: 'bg-blue-600',
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
    phase: 1,
    startAt: 1,
    finishAt: 28,
  },
  {
    id: 'competitive_intelligence',
    name: 'Marcus Chen',
    role: 'Competitive Intelligence',
    task: 'Competitors, share, digital assessment',
    color: 'bg-purple-600',
    textColor: 'text-purple-700',
    bgLight: 'bg-purple-50',
    borderColor: 'border-purple-200',
    phase: 1,
    startAt: 2,
    finishAt: 32,
  },
  {
    id: 'resonate_segmentation',
    name: 'Sofia Ramos',
    role: 'Resonate rAI Specialist',
    task: 'Segments + Resonate taxonomy mapping',
    color: 'bg-pink-600',
    textColor: 'text-pink-700',
    bgLight: 'bg-pink-50',
    borderColor: 'border-pink-200',
    phase: 1,
    startAt: 3,
    finishAt: 44,
    featured: true,
  },
  {
    id: 'opportunities',
    name: 'Kai Morgan',
    role: 'Opportunity Strategist',
    task: 'White-space + growth openings',
    color: 'bg-emerald-600',
    textColor: 'text-emerald-700',
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    phase: 1,
    startAt: 4,
    finishAt: 30,
  },
  {
    id: 'threats',
    name: 'Ivan Petrov',
    role: 'Risk & Threats',
    task: 'Exposure, drivers, macro risks',
    color: 'bg-rose-600',
    textColor: 'text-rose-700',
    bgLight: 'bg-rose-50',
    borderColor: 'border-rose-200',
    phase: 1,
    startAt: 5,
    finishAt: 28,
  },
  {
    id: 'recommendations',
    name: 'Elena Park',
    role: 'Head of Strategy',
    task: 'Tiered actions + KPIs',
    color: 'bg-orange-600',
    textColor: 'text-orange-700',
    bgLight: 'bg-orange-50',
    borderColor: 'border-orange-200',
    phase: 1,
    startAt: 6,
    finishAt: 34,
  },
  {
    id: 'data_sources',
    name: 'James Okonkwo',
    role: 'Research Librarian',
    task: 'Citations, methodology, confidence',
    color: 'bg-teal-600',
    textColor: 'text-teal-700',
    bgLight: 'bg-teal-50',
    borderColor: 'border-teal-200',
    phase: 1,
    startAt: 7,
    finishAt: 26,
  },
  {
    id: 'executive_summary',
    name: 'Priya Mehta',
    role: 'Exec Summary Synthesis',
    task: 'Reads all findings → 8-section memo',
    color: 'bg-indigo-600',
    textColor: 'text-indigo-700',
    bgLight: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    phase: 2,
    startAt: 48,
    finishAt: 62,
    featured: true,
  },
];

const initialsOf = (fullName) =>
  fullName
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const STATUS_QUEUED = 'queued';
const STATUS_WORKING = 'working';
const STATUS_DONE = 'done';

const statusFor = (agent, elapsed) => {
  if (elapsed < agent.startAt) return STATUS_QUEUED;
  if (elapsed < agent.finishAt) return STATUS_WORKING;
  return STATUS_DONE;
};

const StatusBadge = ({ status }) => {
  if (status === STATUS_DONE) {
    return (
      <span
        data-testid="agent-status-done"
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700"
      >
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        Complete
      </span>
    );
  }
  if (status === STATUS_WORKING) {
    return (
      <span
        data-testid="agent-status-working"
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5 animate-pulse" />
        Analyzing…
      </span>
    );
  }
  return (
    <span
      data-testid="agent-status-queued"
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5" />
      Queued
    </span>
  );
};

const AgentCard = ({ agent, status }) => {
  const isDone = status === STATUS_DONE;
  const isWorking = status === STATUS_WORKING;

  return (
    <div
      data-testid={`agent-card-${agent.id}`}
      className={`relative flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
        isWorking
          ? `${agent.bgLight} ${agent.borderColor} shadow-md ring-1 ring-offset-1 ring-orange-300`
          : isDone
          ? 'bg-white border-green-200'
          : 'bg-gray-50 border-gray-200 opacity-70'
      }`}
    >
      {/* Head shot */}
      <div className="relative flex-shrink-0">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shadow ${agent.color}`}
        >
          {initialsOf(agent.name)}
        </div>
        {isWorking && (
          <div
            className="absolute -inset-1 rounded-full border-2 border-orange-400 border-t-transparent animate-spin"
            aria-hidden="true"
          />
        )}
        {isDone && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate">
            <div className="text-sm font-bold text-gray-900 truncate">{agent.name}</div>
            <div className={`text-xs font-medium truncate ${agent.textColor}`}>
              {agent.role}
              {agent.featured && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                  specialist
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <div className="text-[11px] text-gray-500 mt-1 truncate">{agent.task}</div>
      </div>
    </div>
  );
};

const AgentSwarmProgress = ({ active, estimatedDurationSec = 75 }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return undefined;
    }
    const start = Date.now();
    const id = setInterval(() => {
      setElapsed((Date.now() - start) / 1000);
    }, 250);
    return () => clearInterval(id);
  }, [active]);

  const phase1 = AGENTS.filter((a) => a.phase === 1);
  const phase2 = AGENTS.filter((a) => a.phase === 2);

  const completed = AGENTS.filter((a) => statusFor(a, elapsed) === STATUS_DONE).length;
  const total = AGENTS.length;
  const pct = Math.min(100, Math.round((completed / total) * 100));

  const timeLeft = Math.max(0, Math.ceil(estimatedDurationSec - elapsed));
  const currentWorking = AGENTS.find((a) => statusFor(a, elapsed) === STATUS_WORKING);

  return (
    <div data-testid="swarm-progress-panel" className="w-full">
      {/* Header */}
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 mb-3">
          <span className="text-lg" aria-hidden="true">
            🐝
          </span>
          <span className="text-xs font-bold uppercase tracking-wider text-orange-700">
            BCM Agent Swarm · Live
          </span>
        </div>
        <h4 className="text-2xl font-bold text-gray-900">Your analysis team is at work</h4>
        <p className="text-sm text-gray-600 mt-1">
          {completed === total
            ? 'Assembling final report…'
            : currentWorking
            ? `${currentWorking.name} is ${currentWorking.role.toLowerCase()}…`
            : 'Agents spinning up…'}
        </p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            data-testid="swarm-progress-bar"
            className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-xs font-semibold text-gray-700 whitespace-nowrap">
          {completed} / {total} agents
        </div>
        <div className="text-xs text-gray-500 whitespace-nowrap">
          {timeLeft > 0 ? `~${timeLeft}s left` : 'finalizing…'}
        </div>
      </div>

      {/* Phase 1: parallel specialists */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
            Phase 1 · Specialists running in parallel
          </span>
          <span className="flex-1 h-px bg-gray-200" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {phase1.map((agent) => (
            <AgentCard key={agent.id} agent={agent} status={statusFor(agent, elapsed)} />
          ))}
        </div>
      </div>

      {/* Phase 2: synthesis */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
            Phase 2 · Synthesis
          </span>
          <span className="flex-1 h-px bg-gray-200" />
        </div>
        <div className="grid grid-cols-1 gap-2">
          {phase2.map((agent) => (
            <AgentCard key={agent.id} agent={agent} status={statusFor(agent, elapsed)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentSwarmProgress;
